// AccountScreen.tsx
import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { Breadcrumb, Button, Card, Col, Divider, Input, Pagination, Row, Select, Table, Typography } from 'antd'
import {
   HomeOutlined,
   PlusCircleOutlined,
   InfoCircleOutlined,
   EditOutlined,
   DeleteOutlined,
   ExclamationCircleOutlined,
   SearchOutlined
} from '@ant-design/icons'
import { useHistory } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { ROUTER_PATH } from '../routes/metaData'
import useModal from 'src/helpers/modal/useModal'
import { formatCurrency } from 'src/helpers/functions'
import { getPage, getRoleName, PAGE_SIZE, ROLE_ACCOUNT } from 'src/helpers/constants'
import { useStore } from 'src/hooks'
import { isEmpty, size, trim } from 'lodash'
import { useLoading } from 'src/common/components/LoadingContainer'

const { Option } = Select
const { Title } = Typography

const AccountScreen = observer(() => {
   const history = useHistory()
   const { t } = useTranslation()
   const { confirm } = useModal()
   const UserStore = useStore('UserStore')
   const [formSearch, setFormSearch] = useState<any>({})
   const { rows, offset, count } = UserStore.listEmployees
   const [textSearch, set_textSearch] = useState('')
   const { showLoading, hideLoading } = useLoading()
   const [fieldFilter, setFieldFilter] = useState<any>({})

   useEffect(() => {
      if (size(trim(textSearch)) > 0) {
         getListData(0, { ...fieldFilter, ...{ name: trim(textSearch) } })
      } else {
         getListData(0, { ...fieldFilter })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [textSearch, fieldFilter])

   const getListData = async (currentPage: number, param?: any) => {
      try {
         showLoading()
         const offset = getPage(currentPage)
         const params = { offset, ...param }
         console.log('params', params)

         await UserStore.getEmployees(params)
      } catch (error) {
         // console.log(error)
      } finally {
         hideLoading()
      }
   }

   const onChange = (currentPage: number) => {
      if (size(trim(textSearch)) > 0) {
         getListData(currentPage - 1, { ...fieldFilter, ...{ name: trim(textSearch) } })
      } else {
         getListData(currentPage - 1, { ...fieldFilter })
      }
   }

   const handleChangeTextSearch = (value: any) => {
      set_textSearch(value)
   }

   const handleChangeRole = (value: any) => {
      console.log('value', value)
      if (value == fieldFilter.role || !value) {
         let x = { ...fieldFilter }
         delete x['role']
         setFieldFilter(x)
      } else {
         setFieldFilter({ ...fieldFilter, ...{ role: value } })
      }
   }

   const handleRedirectToCreate = useCallback(() => {
      history.push(`/${ROUTER_PATH.AccountCreateScreen}`)
   }, [history])

   const showDetail = useCallback(
      (detail: any) => {
         history.push(`/${ROUTER_PATH.AccountDetailScreen.replace(':id', detail?.id?.toString())}`)
      },
      [history]
   )

   const showEdit = useCallback(
      (detail: any) => {
         UserStore.setUserSelected(detail)
         console.log('detail', detail)

         history.push(`/${ROUTER_PATH.AccountUpdateScreen.replace(':id', detail?.id?.toString())}`)
      },
      [history]
   )

   const handleDelete = useCallback(
      (value: any) => {
         confirm({
            title: t('delete_confirm', { name: value?.name || '', code: value?.phoneNumber || '' }),
            icon: <ExclamationCircleOutlined />,
            okText: t('agree'),
            cancelText: t('cancel'),
            async onOk() {
               await UserStore.deleteEmployees(value?.id)
               if (size(trim(textSearch)) > 0) {
                  getListData(0, { ...fieldFilter, ...{ name: trim(textSearch) } })
               } else {
                  getListData(0, { ...fieldFilter })
               }
            }
         })
      },
      [confirm, t]
   )

   const columns = useMemo(
      () => [
         { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
         { title: 'Họ và tên', dataIndex: 'name', key: 'name' },
         // { title: t('email'), dataIndex: 'email', key: 'email' },
         { title: 'Số điện thoại', dataIndex: 'phoneNumber', key: 'phoneNumber' },
         { title: 'Quyền', dataIndex: 'role', key: 'role', render: (val: any) => getRoleName(val) },
         { title: 'Ví', dataIndex: 'wallet', key: 'wallet', render: (val: any) => val.balance },
         { title: 'Địa chỉ', dataIndex: 'address', key: 'address' },
         {
            title: 'Trạng thái',
            dataIndex: 'status',
            key: 'status',
            render: (val: any) => (val ? 'Hoạt động' : 'Ngừng hoạt động')
         },
         {
            title: 'Xác thực TK lái xe',
            dataIndex: 'isEmailVerified',
            key: 'isEmailVerified',
            render: (val: any, record: any) => (val ? 'Đã xác thực' : 'Chưa xác thực')
         },
         // { title: 'Số tài khoản', dataIndex: 'bankNumber', key: 'bankNumber' },
         // { title: 'Ghi chú', dataIndex: 'note', key: 'note' },
         {
            title: t('actions'),
            key: 'action',
            align: 'center' as 'center',
            width: 120,
            fixed: 'right' as 'right',
            render: (record: any) => (
               <span>
                  {/* <span onClick={() => showDetail(record)} className="gx-link">
                     <InfoCircleOutlined />
                  </span>
                  <Divider type="vertical" /> */}
                  <span onClick={() => showEdit(record)} className="gx-link">
                     <EditOutlined />
                  </span>
                  <Divider type="vertical" />
                  <span onClick={() => handleDelete(record)} className="gx-link">
                     <DeleteOutlined />
                  </span>
               </span>
            )
         }
      ],
      [t, showDetail, showEdit, handleDelete]
   )

   const renderPagination = () => {
      if (isEmpty(rows)) {
         return null
      }

      return (
         <div className="containerPagination">
            <Pagination
               onChange={onChange}
               pageSize={PAGE_SIZE}
               current={offset / PAGE_SIZE + 1}
               showSizeChanger={false}
               total={count}
            />
         </div>
      )
   }

   return (
      <div>
         <Row style={{ padding: '0 20px' }} className="gx-justify-content-between gx-align-items-center">
            <Col>
               <Title level={4}>{'Quản lý tài khoản'}</Title>
               <Breadcrumb>
                  <Breadcrumb.Item href="/">
                     <HomeOutlined className="gx-link" />
                  </Breadcrumb.Item>
                  <Breadcrumb.Item>{'Admin'}</Breadcrumb.Item>
                  <Breadcrumb.Item className="gx-link">{'Quản lý tài khoản'}</Breadcrumb.Item>
               </Breadcrumb>
            </Col>
            <Col>
               <Button icon={<PlusCircleOutlined />} type="primary" onClick={handleRedirectToCreate}>
                  {t('add_new')}
               </Button>
            </Col>
         </Row>

         <Card style={{ marginTop: '20px' }}>
            <Row gutter={16}>
               <Col span={8}>
                  <Input.Search placeholder={t('search')} allowClear onSearch={handleChangeTextSearch} />
               </Col>
               <Col span={8}>
                  <Select
                     placeholder={'Tất cả loại tài khoản'}
                     style={{ width: '100%' }}
                     allowClear
                     onChange={handleChangeRole}
                  >
                     <Select.Option value={ROLE_ACCOUNT.USER}>Khách hàng</Select.Option>
                     <Select.Option value={ROLE_ACCOUNT.DRIVER}>Tài xế</Select.Option>
                     <Select.Option value={ROLE_ACCOUNT.SALE}>Chăm sóc khách hàng</Select.Option>
                     <Select.Option value={ROLE_ACCOUNT.ADMIN}>Admin</Select.Option>
                  </Select>
               </Col>
               <Col span={4}>
                  <Button icon={<SearchOutlined />} type="primary">
                     {t('search')}
                  </Button>
               </Col>
            </Row>

            <Table style={{ marginTop: 20 }} columns={columns} dataSource={rows} rowKey="id" pagination={false} />
            {renderPagination()}
         </Card>
      </div>
   )
})

export default AccountScreen
